/**
 * API Response Validator
 * 
 * Provides robust validation and extraction of station metadata from API responses.
 * Implements multiple extraction strategies to handle different response formats
 * and prevents runtime errors by validating data types before operations.
 * 
 * Key Features:
 * - Multiple extraction strategies for different response formats
 * - Type guards to prevent .match() errors on non-string values
 * - Comprehensive logging for debugging
 * - Graceful fallback handling
 * 
 * @module apiResponseValidator
 */

/**
 * Result of validation operation
 */
export interface ValidationResult<T> {
  /** Whether validation succeeded */
  isValid: boolean;
  /** Validated and extracted data (if successful) */
  data?: T;
  /** Error message (if validation failed) */
  error?: string;
  /** Actual structure of the response (for debugging) */
  actualStructure?: any;
}

/**
 * Expected structure of station metadata API response
 */
export interface StationMetadataResponse {
  success: boolean;
  metadata?: any;
  stations?: any[];
  [key: string]: any;
}

/**
 * API Response Validator
 * 
 * Validates and normalizes API responses to prevent runtime errors.
 * Implements defensive programming patterns and comprehensive error logging.
 */
export class APIResponseValidator {
  /**
   * Validate and extract stations array from metadata response
   * 
   * This method tries multiple extraction strategies to handle different
   * response formats from the metadata API. It validates the response
   * structure and logs detailed information for debugging.
   * 
   * Extraction Strategies (in order):
   * 1. Direct stations array: response.data.stations
   * 2. Nested in metadata with dynamic key: response.data.metadata[key]
   * 3. Nested in metadata.stations: response.data.metadata.stations
   * 4. Alternative formats (extensible for future formats)
   * 
   * @param response - Raw API response object
   * @returns ValidationResult with extracted stations array or error details
   * 
   * @example
   * ```typescript
   * const result = APIResponseValidator.validateStationMetadataResponse(response);
   * if (result.isValid && result.data) {
   *   // Process stations array
   *   result.data.forEach(station => { ... });
   * } else {
   *   console.error('Validation failed:', result.error);
   *   console.log('Actual structure:', result.actualStructure);
   * }
   * ```
   */
  static validateStationMetadataResponse(
    response: any
  ): ValidationResult<any[]> {
    console.log('[APIResponseValidator] Validating station metadata response');
    
    // Step 1: Validate response exists
    if (!response) {
      const error = 'Response is null or undefined';
      console.error(`[APIResponseValidator] ❌ ${error}`);
      return {
        isValid: false,
        error,
        actualStructure: { response: null }
      };
    }
    
    // Step 2: Validate response.data exists
    if (!response.data) {
      const error = 'Response.data is null or undefined';
      console.error(`[APIResponseValidator] ❌ ${error}`);
      return {
        isValid: false,
        error,
        actualStructure: {
          hasResponse: true,
          hasData: false,
          responseKeys: Object.keys(response)
        }
      };
    }
    
    // Step 3: Check success flag
    if (response.data.success === false) {
      const error = response.data.error || 'API returned success: false';
      console.error(`[APIResponseValidator] ❌ ${error}`);
      return {
        isValid: false,
        error,
        actualStructure: {
          success: false,
          errorMessage: response.data.error,
          responseKeys: Object.keys(response.data)
        }
      };
    }
    
    // Step 4: Try multiple extraction strategies
    let stations: any[] | undefined;
    let strategyUsed: string | undefined;
    
    // Strategy 1: Direct stations array
    if (Array.isArray(response.data.stations)) {
      stations = response.data.stations;
      strategyUsed = 'direct-stations-array';
      console.log(`[APIResponseValidator] ✅ Strategy 1: Found direct stations array (${stations?.length ?? 0} stations)`);
    }
    
    // Strategy 2: metadata.stations (check this first as it's most explicit)
    else if (response.data.metadata && typeof response.data.metadata === 'object') {
      if (Array.isArray(response.data.metadata.stations)) {
        stations = response.data.metadata.stations;
        strategyUsed = 'nested-metadata-stations';
        console.log(`[APIResponseValidator] ✅ Strategy 2: Found stations in metadata.stations (${stations?.length ?? 0} stations)`);
      }
      
      // Strategy 3: Nested in metadata with dynamic key (find first array value)
      if (!stations) {
        const keys = Object.keys(response.data.metadata);
        
        for (const key of keys) {
          if (Array.isArray(response.data.metadata[key])) {
            stations = response.data.metadata[key];
            strategyUsed = 'nested-metadata-dynamic-key';
            console.log(`[APIResponseValidator] ✅ Strategy 3: Found stations in metadata['${key}'] (${stations?.length ?? 0} stations)`);
            break;
          }
        }
      }
    }
    
    // Step 5: Validate extracted data
    if (!stations || !Array.isArray(stations)) {
      const error = 'Could not extract stations array from response';
      console.error(`[APIResponseValidator] ❌ ${error}`);
      
      // Build detailed structure information for debugging
      const actualStructure = {
        hasData: !!response.data,
        hasMetadata: !!response.data.metadata,
        metadataType: typeof response.data.metadata,
        metadataKeys: response.data.metadata ? Object.keys(response.data.metadata) : [],
        hasStations: !!response.data.stations,
        stationsType: typeof response.data.stations,
        stationsIsArray: Array.isArray(response.data.stations),
        // Sample first metadata key value type
        firstMetadataKeyType: response.data.metadata && Object.keys(response.data.metadata).length > 0
          ? typeof response.data.metadata[Object.keys(response.data.metadata)[0]]
          : 'N/A'
      };
      
      console.error('[APIResponseValidator] Actual structure:', actualStructure);
      
      return {
        isValid: false,
        error,
        actualStructure
      };
    }
    
    // Step 6: Validate array is not empty (optional warning)
    if (stations.length === 0) {
      console.warn('[APIResponseValidator] ⚠️ Stations array is empty');
    }
    
    console.log(`[APIResponseValidator] ✅ Validation successful: ${stations.length} stations extracted using strategy: ${strategyUsed}`);
    
    return {
      isValid: true,
      data: stations
    };
  }
  
  /**
   * Validate individual station object has required fields
   * 
   * Checks that a station object contains all required fields with correct types.
   * Required fields: stationCode (string), latitude (number), longitude (number)
   * 
   * @param station - Station object to validate
   * @returns true if station has all required fields, false otherwise
   * 
   * @example
   * ```typescript
   * const isValid = APIResponseValidator.validateStationObject({
   *   stationCode: 'CPTR3',
   *   latitude: 51.4775,
   *   longitude: -0.4614
   * }); // Returns true
   * 
   * const isInvalid = APIResponseValidator.validateStationObject({
   *   stationCode: 123, // Wrong type
   *   latitude: 51.4775
   * }); // Returns false
   * ```
   */
  static validateStationObject(station: any): boolean {
    // Validate station exists and is an object
    if (!station || typeof station !== 'object') {
      console.warn('[APIResponseValidator] Station is not an object:', typeof station);
      return false;
    }
    
    // Validate stationCode exists and is a string
    if (!station.stationCode && !station.station_code) {
      console.warn('[APIResponseValidator] Station missing stationCode field');
      return false;
    }
    
    const stationCode = station.stationCode || station.station_code;
    if (typeof stationCode !== 'string') {
      console.warn(`[APIResponseValidator] stationCode is not a string: ${typeof stationCode}`);
      return false;
    }
    
    // Validate latitude exists and is a number (or can be parsed as number)
    const latitude = station.latitude;
    if (latitude === null || latitude === undefined) {
      console.warn('[APIResponseValidator] Station missing latitude field');
      return false;
    }
    
    const latNum = typeof latitude === 'number' ? latitude : parseFloat(latitude);
    if (isNaN(latNum)) {
      console.warn(`[APIResponseValidator] latitude is not a valid number: ${latitude}`);
      return false;
    }
    
    // Validate longitude exists and is a number (or can be parsed as number)
    const longitude = station.longitude;
    if (longitude === null || longitude === undefined) {
      console.warn('[APIResponseValidator] Station missing longitude field');
      return false;
    }
    
    const lonNum = typeof longitude === 'number' ? longitude : parseFloat(longitude);
    if (isNaN(lonNum)) {
      console.warn(`[APIResponseValidator] longitude is not a valid number: ${longitude}`);
      return false;
    }
    
    return true;
  }
  
  /**
   * Safe string extraction with type guards
   * 
   * Prevents .match() errors by ensuring value is a string before returning.
   * Provides type checking and conversion with comprehensive logging.
   * 
   * Type Handling:
   * - null/undefined: returns default value
   * - string: returns as-is
   * - number/boolean: converts to string
   * - object/array: converts to JSON string
   * 
   * @param value - Value to extract as string
   * @param fieldName - Name of field (for logging)
   * @param defaultValue - Default value if extraction fails (default: '')
   * @returns String value or default
   * 
   * @example
   * ```typescript
   * // Safe string extraction
   * const code = APIResponseValidator.safeStringExtract('CPTR3', 'stationCode');
   * // Returns: 'CPTR3'
   * 
   * // Handles null/undefined
   * const empty = APIResponseValidator.safeStringExtract(null, 'country', 'Unknown');
   * // Returns: 'Unknown'
   * 
   * // Converts numbers
   * const num = APIResponseValidator.safeStringExtract(123, 'id');
   * // Returns: '123'
   * 
   * // Prevents .match() errors
   * const value = APIResponseValidator.safeStringExtract(someValue, 'field');
   * const match = value.match(/pattern/); // Safe - value is always a string
   * ```
   */
  static safeStringExtract(
    value: any,
    fieldName: string,
    defaultValue: string = ''
  ): string {
    // Handle null or undefined - CRITICAL: prevents .match() errors
    if (value === null || value === undefined) {
      console.debug(`[APIResponseValidator] Field '${fieldName}' is null/undefined, using default: "${defaultValue}"`);
      return defaultValue;
    }
    
    // If already a string, return it immediately
    if (typeof value === 'string') {
      return value;
    }
    
    // Convert numbers and booleans to string
    if (typeof value === 'number' || typeof value === 'boolean') {
      const converted = String(value);
      console.debug(`[APIResponseValidator] Field '${fieldName}' converted from ${typeof value} to string: "${converted}"`);
      return converted;
    }
    
    // Convert objects/arrays to JSON string
    if (typeof value === 'object') {
      try {
        const converted = JSON.stringify(value);
        console.warn(`[APIResponseValidator] Field '${fieldName}' is an object, converted to JSON string`);
        return converted;
      } catch (e) {
        console.error(`[APIResponseValidator] Failed to stringify object for field '${fieldName}':`, e);
        return defaultValue;
      }
    }
    
    // Fallback: convert to string
    try {
      const converted = String(value);
      console.warn(`[APIResponseValidator] Field '${fieldName}' has unexpected type (${typeof value}), converted to string: "${converted}"`);
      return converted;
    } catch (e) {
      console.error(`[APIResponseValidator] Failed to convert field '${fieldName}' to string:`, e);
      return defaultValue;
    }
  }
}
